import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Check, X, Calendar, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section 18 — Public "Yes / No" attendance confirmation page.
 * No authentication required — token is the credential.
 */
export default function AttendanceConfirm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [state, setState] = useState(null); // fetched appointment payload
  const [submitting, setSubmitting] = useState("");
  const [reason, setReason] = useState("");
  const [showReason, setShowReason] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // "yes" | "no" after submit

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/attendance/${token}`)
      .then((r) => { setState(r.data); if (r.data?.response) setConfirmation(r.data.response); })
      .catch((e) => {
        const detail = e?.response?.data?.detail || "invalid_or_expired";
        setError(String(detail));
      })
      .finally(() => setLoading(false));
  }, [token]);

  const respond = async (choice) => {
    if (submitting) return;
    setSubmitting(choice);
    try {
      const url = `${API}/attendance/${token}/${choice}`;
      const body = choice === "no" ? { reason } : {};
      await axios.post(url, body);
      setConfirmation(choice);
    } catch (e) {
      const detail = e?.response?.data?.detail || "network_error";
      setError(String(detail));
    } finally {
      setSubmitting("");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F0]" data-testid="attendance-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#9A7B4F]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F0] px-6" data-testid="attendance-error">
        <div className="max-w-md w-full text-center bg-white border border-[#9A7B4F]/20 p-10">
          <X className="w-10 h-10 mx-auto text-[#B23A48] mb-4" strokeWidth={1.5} />
          <h1 className="font-serif text-2xl text-[#282828] mb-3">This link is no longer valid</h1>
          <p className="text-sm text-[#676664] leading-relaxed">
            {error === "link_expired" && "The confirmation window for this appointment has closed."}
            {error === "link_invalidated" && "This link was invalidated because the appointment was updated. Please check your latest email/SMS for a fresh link."}
            {error === "invalid_or_expired" && "The link you followed doesn't match any appointment we can find."}
            {error === "appointment_missing" && "The appointment associated with this link no longer exists."}
            {!["link_expired","link_invalidated","invalid_or_expired","appointment_missing"].includes(error) && "Please contact Mahra support for help."}
          </p>
          <p className="mt-6 text-[10px] tracking-widest uppercase text-[#9A7B4F]">Mahra — Melbourne studio</p>
        </div>
      </div>
    );
  }

  if (confirmation) {
    const yes = confirmation === "yes";
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F0] px-6" data-testid={`attendance-confirmed-${confirmation}`}>
        <div className="max-w-md w-full text-center bg-white border border-[#9A7B4F]/20 p-10">
          {yes
            ? <Check className="w-12 h-12 mx-auto text-[#0E7C5A] mb-4" strokeWidth={1.5} />
            : <X className="w-12 h-12 mx-auto text-[#B23A48] mb-4" strokeWidth={1.5} />}
          <h1 className="font-serif text-2xl text-[#282828] mb-3">
            {yes ? "You're confirmed" : "Thanks for letting us know"}
          </h1>
          <p className="text-sm text-[#676664] leading-relaxed">
            {yes
              ? "We've noted you'll be attending. We look forward to seeing you at the studio."
              : "We've recorded that you can't make it. Our team will reach out shortly to reschedule or cancel."}
          </p>
          {state?.date ? (
            <p className="text-xs text-[#9A7B4F] tracking-widest uppercase mt-6">
              {state.date}{state.time ? ` · ${state.time}` : ""}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF8F0] px-6 py-10">
      <div className="max-w-md w-full bg-white border border-[#9A7B4F]/20 p-10" data-testid="attendance-form">
        <p className="text-[10px] tracking-widest uppercase text-[#9A7B4F] mb-3">Mahra — Trial appointment</p>
        <h1 className="font-serif text-3xl text-[#282828] leading-tight mb-4">
          Will you be attending your trial?
        </h1>
        <p className="text-sm text-[#676664] mb-6 leading-relaxed">
          Hi {state?.customer_name || "there"}, please confirm below so our team can prepare for your visit.
        </p>

        <div className="border border-[#9A7B4F]/25 p-4 mb-6 bg-[#FAF8F0]/60">
          <div className="flex items-center gap-2 text-[#282828] text-sm mb-1">
            <Calendar className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
            <span data-testid="attendance-when">
              {state?.date}{state?.time ? ` · ${state.time}` : ""}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[#676664] text-xs">
            <MapPin className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={1.5} />
            <span>{state?.location_label || "Melbourne studio"}</span>
          </div>
        </div>

        {!showReason ? (
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => respond("yes")}
              disabled={!!submitting}
              className="w-full bg-[#9A7B4F] hover:bg-[#8A6D45] text-white uppercase tracking-widest text-xs py-6"
              data-testid="attendance-yes-btn"
            >
              {submitting === "yes"
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : "Yes, I'll attend"}
            </Button>
            <Button
              onClick={() => setShowReason(true)}
              disabled={!!submitting}
              variant="outline"
              className="w-full border-[#9A7B4F]/40 text-[#282828] uppercase tracking-widest text-xs py-6"
              data-testid="attendance-no-open-btn"
            >
              No, I can&apos;t attend
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <label className="text-xs text-[#676664] tracking-wider uppercase">
              Optional — let us know why (max 500 chars)
            </label>
            <textarea
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full border border-[#9A7B4F]/30 bg-white p-3 text-sm text-[#282828] focus:outline-none focus:border-[#9A7B4F]"
              data-testid="attendance-reason-input"
              placeholder="Feel free to share the reason, or leave blank."
            />
            <div className="flex gap-2 mt-2">
              <Button
                onClick={() => respond("no")}
                disabled={!!submitting}
                className="flex-1 bg-[#B23A48] hover:bg-[#8f2b37] text-white uppercase tracking-widest text-xs py-6"
                data-testid="attendance-no-submit-btn"
              >
                {submitting === "no"
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : "Submit"}
              </Button>
              <Button
                onClick={() => { setShowReason(false); setReason(""); }}
                variant="outline"
                className="border-[#9A7B4F]/40 text-[#282828] uppercase tracking-widest text-xs py-6"
                data-testid="attendance-no-back-btn"
              >
                Back
              </Button>
            </div>
          </div>
        )}

        <p className="text-[10px] text-[#9A7B4F]/70 tracking-widest uppercase text-center mt-8">
          Secure link — no login required
        </p>
      </div>
    </div>
  );
}
